/**
 * EditableTable.jsx - a plain editable grid for the Quote Rates lists (rate
 * groups, tasks, packages, holidays, customers, locations). Columns say how
 * each cell edits: text, number, date, check (0/1), or select. Read-only
 * shows the same grid with nothing editable and no add / remove buttons.
 */
import { Btn, LINE, MUTED, inputStyle } from '../quotes/quoteUi';

const cellInput = { ...inputStyle, padding: '4px 6px', fontSize: 11.5 };

export default function EditableTable({ columns, rows, onChange, readOnly, blank, label }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: c.type === 'number' || c.type === 'check' ? 'center' : 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  color: MUTED,
                  textTransform: 'uppercase',
                  padding: '4px 6px',
                  borderBottom: `1px solid ${LINE}`,
                  width: c.width,
                }}
              >
                {c.label}
              </th>
            ))}
            {!readOnly && <th style={{ width: 30, borderBottom: `1px solid ${LINE}` }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => {
                const v = r[c.key];
                let el;
                if (c.type === 'check') {
                  el = (
                    <input
                      type="checkbox"
                      aria-label={`${c.label} row ${i + 1}`}
                      checked={!!Number(v)}
                      disabled={readOnly}
                      onChange={(e) => set(i, c.key, e.target.checked ? 1 : 0)}
                    />
                  );
                } else if (c.type === 'select') {
                  el = (
                    <select
                      aria-label={`${c.label} row ${i + 1}`}
                      value={v ?? ''}
                      disabled={readOnly}
                      onChange={(e) => set(i, c.key, e.target.value)}
                      style={cellInput}
                    >
                      {(c.options || []).includes(v) || !v ? null : <option value={v}>{v}</option>}
                      {(c.options || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  );
                } else {
                  el = (
                    <input
                      aria-label={`${c.label} row ${i + 1}`}
                      type={c.type === 'date' ? 'date' : 'text'}
                      value={v ?? ''}
                      readOnly={readOnly}
                      inputMode={c.type === 'number' ? 'decimal' : undefined}
                      onChange={(e) => set(i, c.key, c.type === 'number' ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
                      style={{
                        ...cellInput,
                        textAlign: c.type === 'number' ? 'right' : 'left',
                        ...(readOnly ? { background: '#F7F8F5' } : null),
                      }}
                    />
                  );
                }
                return (
                  <td key={c.key} style={{ padding: '2px 4px', textAlign: c.type === 'check' ? 'center' : 'left' }}>
                    {el}
                  </td>
                );
              })}
              {!readOnly && (
                <td style={{ padding: '2px 4px' }}>
                  <Btn small danger aria-label={`Remove row ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                    x
                  </Btn>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && (
        <div style={{ marginTop: 6 }}>
          <Btn small onClick={() => onChange([...rows, { ...(blank || {}) }])}>
            + Add row
          </Btn>
        </div>
      )}
    </div>
  );
}
